import { useState } from "react";
import axios from "axios";
import { API, SITE } from "../config";
import { usePageMeta } from "../lib/usePageMeta";
import { track } from "../lib/analytics";
import { Reveal } from "../components/motion/Reveal";

export default function ContactPage({ kind = "contact" }) {
  const isSupport = kind === "support";
  usePageMeta(
    isSupport ? "Support — HUMN" : "Contact — HUMN",
    isSupport ? "Get help with your HUMN account, Credits and experiences." : "Get in touch with the HUMN team."
  );
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "", website: "" });
  const [state, setState] = useState("idle");
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Please enter a valid email.";
    if (!form.message.trim() || form.message.trim().length < 10) e.message = "Please write at least 10 characters.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setState("loading");
    try {
      await axios.post(`${API}/contact`, { ...form, kind });
      setState("success");
      track("contact_submitted", { kind });
    } catch {
      setState("error");
    }
  };

  const field = (name, label, type = "text") => (
    <div>
      <label htmlFor={`${kind}-${name}`} className="block text-sm font-medium mb-2">{label}</label>
      <input
        id={`${kind}-${name}`}
        data-testid={`${kind}-${name}-input`}
        type={type}
        value={form[name]}
        onChange={(e) => setForm({ ...form, [name]: e.target.value })}
        aria-invalid={!!errors[name]}
        className="glass-2 w-full rounded-2xl px-5 py-3.5 text-sm tx-1 focus:outline-none"
        style={{ color: "var(--text-1)" }}
      />
      {errors[name] && <p role="alert" data-testid={`${kind}-${name}-error`} className="text-rose-500 text-xs mt-1.5">{errors[name]}</p>}
    </div>
  );

  return (
    <main className="scene scene-midnight pt-36 pb-28 px-6 min-h-screen" data-testid={`${kind}-page`}>
      <div className="max-w-5xl mx-auto grid lg:grid-cols-2 gap-16">
        <div>
          <Reveal>
            <p className="eyebrow mb-5">{isSupport ? "HUMN Support" : "Say hello"}</p>
            <h1 className="h-display h-lg">{isSupport ? "How can we help?" : "Talk to the HUMN team."}</h1>
            <p className="mt-6 tx-2 leading-relaxed max-w-sm">
              {isSupport
                ? "Questions about your account, Credits, Happenings, safety or anything in the app — we're here."
                : "Partnerships, press, feedback or something else entirely. We read everything."}
            </p>
            <p className="mt-8 text-sm tx-2">
              Prefer email?{" "}
              <a className="underline underline-offset-4" href={`mailto:${isSupport ? SITE.supportEmail : SITE.contactEmail}`} data-testid={`${kind}-email-link`}>
                {isSupport ? SITE.supportEmail : SITE.contactEmail}
              </a>
            </p>
          </Reveal>
        </div>
        <Reveal delay={0.1}>
          {state === "success" ? (
            <div className="glass-3 glass-reflect rounded-3xl p-10 text-center" data-testid={`${kind}-success-state`}>
              <span className="relative z-10 w-12 h-12 rounded-full mx-auto flex items-center justify-center" style={{ background: "linear-gradient(135deg,var(--purple),var(--pink))" }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"><path d="M20 6L9 17l-5-5" /></svg>
              </span>
              <h2 className="h-display text-2xl mt-6 relative z-10">Message sent.</h2>
              <p className="tx-2 mt-3 text-sm leading-relaxed relative z-10">We'll get back to you at {form.email}.</p>
            </div>
          ) : (
            <form onSubmit={submit} noValidate className="space-y-5" data-testid={`${kind}-form`}>
              <input type="text" name="website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
              {field("name", "Name")}
              {field("email", "Email", "email")}
              {field("subject", isSupport ? "Topic" : "Subject")}
              <div>
                <label htmlFor={`${kind}-message`} className="block text-sm font-medium mb-2">Message</label>
                <textarea
                  id={`${kind}-message`}
                  data-testid={`${kind}-message-input`}
                  rows={5}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  aria-invalid={!!errors.message}
                  className="glass-2 w-full rounded-2xl px-5 py-3.5 text-sm tx-1 focus:outline-none resize-none"
                  style={{ color: "var(--text-1)" }}
                />
                {errors.message && <p role="alert" data-testid={`${kind}-message-error`} className="text-rose-500 text-xs mt-1.5">{errors.message}</p>}
              </div>
              <button type="submit" data-testid={`${kind}-submit-btn`} disabled={state === "loading"} className="btn-cta px-8 py-3.5 text-sm font-semibold disabled:opacity-60">
                {state === "loading" ? "Sending…" : "Send message"}
              </button>
              {state === "error" && <p role="alert" data-testid={`${kind}-error-msg`} className="text-rose-500 text-sm">Something went wrong. Please try again.</p>}
            </form>
          )}
        </Reveal>
      </div>
    </main>
  );
}
